import { createContext, useCallback, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  getSelectorPreferences,
  saveSelectorPreferences,
  type SelectorPreferences,
  type SelectorPreferenceType,
} from '@/services/api/selectorPreferencesApi';

/**
 * Whose favourites and recently used items the selectors show. Null keeps them in
 * this browser's localStorage (demo mode and screens outside the timesheet grid).
 */
export interface SelectorPreferencesOwner {
  employeeId: number;
  companyId?: string;
  /** True when the owner is the logged-in user, whose localStorage lists are migrated on first load. */
  isCurrentUser: boolean;
}

export const SelectorPreferencesOwnerContext = createContext<SelectorPreferencesOwner | null>(null);

export function useSelectorPreferencesOwner(): SelectorPreferencesOwner | null {
  return useContext(SelectorPreferencesOwnerContext);
}

type Updater = (prev: SelectorPreferences) => SelectorPreferences;

const EMPTY: SelectorPreferences = { favourites: [], recentlyUsed: [] };
const SAVE_DEBOUNCE_MS = 300;

export function readLocalSelectorPreferences(storageKey: string): SelectorPreferences {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        favourites: parsed.favourites || [],
        recentlyUsed: parsed.recentlyUsed || [],
      };
    }
  } catch (error) {
    console.error('Failed to load selector state:', error);
  }
  return EMPTY;
}

function cacheKey(owner: SelectorPreferencesOwner, selectorType: SelectorPreferenceType): string {
  return `selector-preferences-cache|${storeKey(owner, selectorType)}`;
}

function readCache(key: string): SelectorPreferences | null {
  try {
    const stored = localStorage.getItem(key);
    return stored ? readLocalSelectorPreferences(key) : null;
  } catch {
    return null;
  }
}

function writeCache(entry: StoreEntry) {
  try {
    localStorage.setItem(cacheKey(entry.owner, entry.selectorType), JSON.stringify(entry.state));
  } catch (error) {
    console.error('Failed to cache selector preferences:', error);
  }
}

/** Last known lists for the owner, shown until the server responds. */
function initialState(
  owner: SelectorPreferencesOwner,
  selectorType: SelectorPreferenceType,
  legacyStorageKey: string,
): SelectorPreferences {
  const cached = readCache(cacheKey(owner, selectorType));
  if (cached) return cached;
  return owner.isCurrentUser ? readLocalSelectorPreferences(legacyStorageKey) : EMPTY;
}

interface StoreEntry {
  owner: SelectorPreferencesOwner;
  selectorType: SelectorPreferenceType;
  legacyStorageKey: string;
  state: SelectorPreferences;
  status: 'idle' | 'loading' | 'loaded' | 'failed';
  pending: Updater[];
  listeners: Set<() => void>;
  saveTimer: ReturnType<typeof setTimeout> | null;
  saveChain: Promise<void>;
}

const entries = new Map<string, StoreEntry>();

function storeKey(owner: SelectorPreferencesOwner, selectorType: SelectorPreferenceType): string {
  return `${owner.employeeId}|${owner.companyId ?? ''}|${selectorType}`;
}

function ensureEntry(
  owner: SelectorPreferencesOwner,
  selectorType: SelectorPreferenceType,
  legacyStorageKey: string,
): StoreEntry {
  const key = storeKey(owner, selectorType);
  let entry = entries.get(key);
  if (!entry) {
    entry = {
      owner,
      selectorType,
      legacyStorageKey,
      state: initialState(owner, selectorType, legacyStorageKey),
      status: 'idle',
      pending: [],
      listeners: new Set(),
      saveTimer: null,
      saveChain: Promise.resolve(),
    };
    entries.set(key, entry);
  }
  return entry;
}

function notify(entry: StoreEntry) {
  entry.listeners.forEach(listener => listener());
}

function scheduleSave(entry: StoreEntry) {
  if (entry.saveTimer) clearTimeout(entry.saveTimer);
  entry.saveTimer = setTimeout(() => {
    entry.saveTimer = null;
    entry.saveChain = entry.saveChain
      .then(() => saveSelectorPreferences(entry.owner.employeeId, entry.selectorType, entry.state, entry.owner.companyId))
      .then(
        () => undefined,
        error => console.error('Failed to save selector preferences:', error),
      );
  }, SAVE_DEBOUNCE_MS);
}

async function load(entry: StoreEntry) {
  entry.status = 'loading';
  let loaded: SelectorPreferences = EMPTY;
  let migrated = false;
  try {
    const dto = await getSelectorPreferences(entry.owner.employeeId, entry.selectorType, entry.owner.companyId);
    if (dto.exists) {
      loaded = { favourites: dto.favourites ?? [], recentlyUsed: dto.recentlyUsed ?? [] };
    } else if (entry.owner.isCurrentUser) {
      const legacy = readLocalSelectorPreferences(entry.legacyStorageKey);
      if (legacy.favourites.length > 0 || legacy.recentlyUsed.length > 0) {
        loaded = legacy;
        migrated = true;
      }
    }
    entry.status = 'loaded';
  } catch (error) {
    console.error('Failed to load selector preferences:', error);
    // Without the stored lists a save would overwrite them, so changes stay local.
    entry.status = 'failed';
    entry.pending = [];
    return;
  }

  const hadPending = entry.pending.length > 0;
  entry.state = entry.pending.reduce((state, updater) => updater(state), loaded);
  entry.pending = [];
  writeCache(entry);
  if (migrated || hadPending) scheduleSave(entry);
  notify(entry);
}

function update(entry: StoreEntry, updater: Updater) {
  if (entry.status === 'idle' || entry.status === 'loading') entry.pending.push(updater);
  entry.state = updater(entry.state);
  if (entry.status === 'loaded') {
    writeCache(entry);
    scheduleSave(entry);
  }
  notify(entry);
}

function subscribe(entry: StoreEntry, listener: () => void): () => void {
  entry.listeners.add(listener);
  if (entry.status === 'idle') void load(entry);
  return () => {
    entry.listeners.delete(listener);
    if (entry.listeners.size === 0 && entry.status === 'failed') {
      entries.delete(storeKey(entry.owner, entry.selectorType));
    }
  };
}

/** Clears the in-memory store. Exposed for tests. */
export function resetSelectorPreferencesStore() {
  entries.forEach(entry => {
    if (entry.saveTimer) clearTimeout(entry.saveTimer);
  });
  entries.clear();
}

const noopSubscribe = () => () => {};

/**
 * Favourites and recently used items for a selector. With an owner they are loaded
 * from and saved to the server for that employee, shared by every selector showing
 * the same owner, and cached in localStorage so the last known lists show instantly;
 * without one they live in localStorage under `localStorageKey`.
 */
export function useSelectorPreferences(
  selectorType: SelectorPreferenceType,
  localStorageKey: string,
  owner: SelectorPreferencesOwner | null,
): [SelectorPreferences, (updater: Updater) => void] {
  const ownerEmployeeId = owner?.employeeId;
  const ownerCompanyId = owner?.companyId;
  const ownerIsCurrentUser = owner?.isCurrentUser ?? false;

  const getEntry = useCallback(
    () => ownerEmployeeId == null
      ? null
      : ensureEntry(
          { employeeId: ownerEmployeeId, companyId: ownerCompanyId, isCurrentUser: ownerIsCurrentUser },
          selectorType,
          localStorageKey,
        ),
    [ownerEmployeeId, ownerCompanyId, ownerIsCurrentUser, selectorType, localStorageKey],
  );

  const serverSubscribe = useCallback(
    (listener: () => void) => {
      const entry = getEntry();
      return entry ? subscribe(entry, listener) : noopSubscribe();
    },
    [getEntry],
  );
  const serverState = useSyncExternalStore(serverSubscribe, () => getEntry()?.state ?? EMPTY);

  // localStorage mode
  const isLocal = ownerEmployeeId == null;
  const storageKeyRef = useRef(localStorageKey);
  const skipNextSaveRef = useRef(false);
  const [localState, setLocalState] = useState<SelectorPreferences>(
    () => (isLocal ? readLocalSelectorPreferences(localStorageKey) : EMPTY),
  );

  useEffect(() => {
    if (!isLocal) return;
    const keyChanged = storageKeyRef.current !== localStorageKey;
    storageKeyRef.current = localStorageKey;
    if (!keyChanged) return;

    skipNextSaveRef.current = true;
    setLocalState(readLocalSelectorPreferences(localStorageKey));
  }, [isLocal, localStorageKey]);

  useEffect(() => {
    if (!isLocal) return;
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    try {
      localStorage.setItem(storageKeyRef.current, JSON.stringify(localState));
    } catch (error) {
      console.error('Failed to save selector state:', error);
    }
  }, [isLocal, localState]);

  const updateState = useCallback(
    (updater: Updater) => {
      const entry = getEntry();
      if (entry) {
        update(entry, updater);
      } else {
        setLocalState(updater);
      }
    },
    [getEntry],
  );

  return [isLocal ? localState : serverState, updateState];
}
